import { memo, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cloneDeep, has, isEmpty, isFunction, isNil, isObject } from 'lodash';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import { setDynamicSegmentMainSegmentInitialData } from 'store/slices/dynamicSegment';
import { selectBaseQueryConfigurations } from 'store/slices/queryConfigurations';
// Import Services
import { ReportHttpService, CustomerJourneyHttpService, SegmentHttpService, UtilsHttpService } from 'services/http';
import { getQueryConfigurationsList } from 'services/segment';
// Import Hooks
import useGoToRoute from 'hooks/GoToRoute';
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useQueryParams from 'hooks/UseQueryParams';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import UI Components
import { Modal, Button, BusyLoader } from '@geneui/components';
// Import Components
import KeyValueItemsGroup from 'components/common/KeyValueItemsGroup';
import ColumnKPIsAndFiltersInfo from './ColumnKPIsAndFiltersInfo';
import CustomerJourneyFiltersAndKPIs from './CustomerJourneyFiltersAndKPIs';
import CompositeReportInfoTabs from './CompositeReportInfoTabs';
import CompositeReportGeneralInfoTabs from './CompositeReportGeneralInfoTabs';
import ReportActionsButtons from 'components/ReportList/ReportActionsButtons';
// Import Constants
import { ModalsActions, l, ReportTypesLabels, ReportStateLabels, TimeZonePack, JSON_OBJECT } from 'constants/common';
import { generalDetailsGroupData } from './config';
import { RoutesList } from 'routes';
import { modalsStateSetDictKeys } from 'components/ReportList/config';
//Import Styles
import 'assets/scss/reportInfoModal.scss';

const {
    REPORT_EXECUTED_LIST,
    REPORT_EDIT,
    REPORT_CLONE,
    REPORT_SCHEDULE,
    REPORT_EXECUTE,
    REPORT_COMPOSITE_EDIT,
    REPORT_COMPOSITE_CLONE,
    REPORT_CUSTOMER_JOURNEY_EDIT,
    REPORT_CUSTOMER_JOURNEY_CLONE,
} = RoutesList;

const ReportInfoModal = ({ reportInfo, isVisibleModal, onCancel, handleInfoModalActionClick, predefinedKeyValue }) => {
    const { t } = useTranslation();
    const { getRoutePath, goToPath } = useGoToRoute();
    const { doGetRequest, doPostRequest } = useRequest();
    const { search } = useLocation();
    const dispatch = useAppDispatch();
    const dateFormats = usePartnerDateFormats();
    const goToRouteQueryParams = useQueryParams();

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingUsedIn, setIsLoadingUsedIn] = useState(false);
    const [isLoadingCustomerJourney, setIsLoadingCustomerJourney] = useState(true);
    const [reportAdditionalInfo, setReportAdditionalInfo] = useState<Nullable<AnyObject>>(null);
    const [reportUsedInData, setReportUsedInData] = useState(null);
    const [keyValueItemsGroupData, setKeyValueItemsGroupData] = useState(
        cloneDeep(generalDetailsGroupData(t, dateFormats)),
    );
    const [customerJourney, setCustomerJourney] = useState<AnyObject>({});
    const [recurrenceDataLocal, setRecurrenceDataLocal] = useState<AnyObject>({});
    const [segmentFiltersData, setSegmentFiltersData] = useState<Nullable<AnyObject>>(null);

    const queryConfigurations = useAppSelector(selectBaseQueryConfigurations);

    const { ReportId, isViewMode, ReportType, ReportState, SelfExecutionCount, CompositeExecutionCount } = reportInfo;
    const isReport = ReportType === ReportTypesLabels.Report;
    const isCustomerJourneyReport = ReportType === ReportTypesLabels.CustomerJourneyReport;

    // Actions part
    const deleteReportAction = (report) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.deleteReport, report);
    };

    const getExecuteReportPath = (report) => {
        const { ReportId, ReportType, Name } = report;
        const params = { reportId: ReportId, reportType: ReportType, reportName: Name };
        return getRoutePath(REPORT_EXECUTE, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const executeReportAction = (report) => {
        if (report.HasParameters) {
            const path = getExecuteReportPath(report);
            goToPath(path);
        } else {
            handleInfoModalActionClick(modalsStateSetDictKeys.executeReport, report);
        }
    };

    const getScheduleReportPath = (report) => {
        const { ReportId, ReportType, Name } = report;
        const params = { reportId: ReportId, reportType: ReportType, reportName: Name };
        return getRoutePath(REPORT_SCHEDULE, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const stopScheduleReportAction = (report) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.stopScheduleReport, report);
    };

    const getReportVersionListPath = (report) => {
        const { ReportId, ReportType } = report;
        const params = { reportId: ReportId, reportType: ReportType };
        return getRoutePath(REPORT_EXECUTED_LIST, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const getCloneReportPath = (report) => {
        const { ReportId, ReportType } = report;
        const params = { reportId: ReportId };

        const route =
            ReportType === ReportTypesLabels.Report
                ? REPORT_CLONE
                : ReportType === ReportTypesLabels.CompositeReport
                  ? REPORT_COMPOSITE_CLONE
                  : REPORT_CUSTOMER_JOURNEY_CLONE;

        return getRoutePath(route, params);
    };

    const getEditReportPath = (report) => {
        const { ReportId, ReportType } = report;
        const params = { reportId: ReportId };

        const route =
            ReportType === ReportTypesLabels.Report
                ? REPORT_EDIT
                : ReportType === ReportTypesLabels.CompositeReport
                  ? REPORT_COMPOSITE_EDIT
                  : REPORT_CUSTOMER_JOURNEY_EDIT;

        return getRoutePath(route, params, goToRouteQueryParams.encode({ backParams: search }));
    };

    const goToEditReport = (report) => {
        const path = getEditReportPath(report);
        goToPath(path);
        onCancel();
    };

    const editReportAction = (report) => {
        if (report.ReportType === ReportTypesLabels.Report) {
            doPostRequest(getReportsByReport.request, {
                requestBody: {
                    reportId: report.ReportId,
                    reportType: report.ReportType,
                },
                successCallback: (Data) => {
                    if (isEmpty(Data)) {
                        goToEditReport(report);
                        onCancel();
                    } else {
                        handleInfoModalActionClick(modalsStateSetDictKeys.editReport, {
                            objects: Data.map(({ Name, Type }) => ({ Name, Type })),
                            report,
                        });
                    }
                },
            });
        } else {
            goToEditReport(report);
        }
    };

    const archiveReportAction = (report) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.archiveReport, report);
    };

    const unarchiveReportAction = (report) => {
        handleInfoModalActionClick(modalsStateSetDictKeys.unarchiveReport, report);
    };

    const executionCount = useMemo(() => {
        switch (ReportType) {
            case ReportTypesLabels.Report:
                return `${SelfExecutionCount ?? t(l.NA)} / ${CompositeExecutionCount ?? t(l.NA)}`;
            case ReportTypesLabels.CompositeReport:
                return `${SelfExecutionCount}`;
            case ReportTypesLabels.CustomerJourneyReport:
                return `${SelfExecutionCount}`;
            default:
                return '';
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const title = useMemo(() => {
        switch (ReportType) {
            case ReportTypesLabels.Report:
                return t(l.ReportInfo);
            case ReportTypesLabels.CompositeReport:
                return t(l.CompositeReportInfo);
            case ReportTypesLabels.CustomerJourneyReport:
                return t(l.CustomerJourneyReportInfo);
            default:
                return '';
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { getReportById, getReportsByReport, getCustomerJourneyPreviewById, getScheduleRunDatesRequest } =
        useCancelableRequests({
            getReportById: ReportHttpService.getReportById,
            getReportsByReport: ReportHttpService.getReportsByReport,
            getCustomerJourneyPreviewById: CustomerJourneyHttpService.getCustomerJourneyPreviewById,
            getSegmentCount: SegmentHttpService.getSegmentCount,
            getScheduleRunDatesRequest: UtilsHttpService.getScheduleRunDates,
        });
    // Not an HTTP request object (its request returns void), so it stays out of useCancelableRequests
    const { getQueryConfigurationsListRequest } = useMemo(
        () => ({
            getQueryConfigurationsListRequest: getQueryConfigurationsList(),
        }),
        [],
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const keyValueItemsDataSetter = (key: string, value: any, data: any = undefined, isLoading = false) => {
        setKeyValueItemsGroupData((prev) => {
            const tmpResult = cloneDeep(prev).map((group) => {
                if (group.keyValueItemsData[key]) {
                    const item = group.keyValueItemsData[key];
                    if (has(item, 'renderer') && isObject(value)) {
                        item.value = item.renderer(value);
                    } else {
                        item.value = value;
                    }
                    if (has(item, 'labelRenderer')) {
                        item.label = item.labelRenderer(data);
                    }
                    if (has(item, 'format')) {
                        if (!isNil(data)) {
                            item.value = item.format(data);
                        } else {
                            item.value = item.format(value);
                        }
                    }
                    item.isLoading = isLoading;
                    group.keyValueItemsData[key] = item;
                }
                return group;
            });
            return tmpResult;
        });
    };

    useEffect(() => {
        if (reportAdditionalInfo && ReportType === ReportTypesLabels.Report) {
            keyValueItemsDataSetter('numberOfCustomers', {
                filtersConfig: reportAdditionalInfo?.QueryConfiguration,
            });
            keyValueItemsDataSetter('unifiedCurrency', {
                unifiedCurrency: reportAdditionalInfo.QueryConfiguration.UnifiedCurrency,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportAdditionalInfo]);

    useEffect(() => {
        if (!isNil(reportAdditionalInfo)) {
            keyValueItemsDataSetter('reportId', reportAdditionalInfo.ReportId);
            keyValueItemsDataSetter('name', reportAdditionalInfo.Name);
            keyValueItemsDataSetter('description', reportAdditionalInfo.Description);
            keyValueItemsDataSetter('executionCount', executionCount);
        }
    }, [reportAdditionalInfo, executionCount]);

    useEffect(() => {
        if (!isEmpty(predefinedKeyValue)) {
            keyValueItemsDataSetter('regions', predefinedKeyValue.regions);
            keyValueItemsDataSetter('categories', predefinedKeyValue.categories);
        }
    }, [predefinedKeyValue]);

    useEffect(() => {
        if (!isNil(ReportType)) {
            keyValueItemsDataSetter('type', { type: ReportType });
        }
    }, [ReportType]);

    useEffect(() => {
        if (isReport) {
            keyValueItemsDataSetter('usedIn', reportUsedInData, null, isLoadingUsedIn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportUsedInData, isLoadingUsedIn]);

    useEffect(() => {
        if (isCustomerJourneyReport) {
            keyValueItemsDataSetter('blockName', customerJourney.blockName, null, isLoadingCustomerJourney);
            keyValueItemsDataSetter('customerJourneyName', customerJourney.name, null, isLoadingCustomerJourney);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerJourney, isLoadingCustomerJourney]);

    useEffect(() => {
        if (!isNil(reportAdditionalInfo?.CompositeConfiguration)) {
            keyValueItemsDataSetter('operation', {
                operation: reportAdditionalInfo?.CompositeConfiguration.Operation,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportAdditionalInfo]);

    useEffect(() => {
        if (!isNil(ReportState)) {
            keyValueItemsDataSetter('reportState', t(ReportStateLabels[ReportState]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ReportState]);

    useEffect(() => {
        setKeyValueItemsGroupData((prev) => {
            return prev.map((item) => ({
                ...item,
                isVisible: item.key !== 'schedule' || !isNil(reportAdditionalInfo?.Schedule),
            }));
        });
        if (!isNil(reportAdditionalInfo?.Schedule)) {
            keyValueItemsDataSetter(
                'startDate',
                reportAdditionalInfo?.Schedule?.StartDate,
                reportAdditionalInfo?.Schedule,
            );
            keyValueItemsDataSetter('endDate', reportAdditionalInfo?.Schedule?.EndDate, reportAdditionalInfo?.Schedule);
            if (reportAdditionalInfo?.Schedule?.RecurrenceCount !== 0) {
                keyValueItemsDataSetter('recurrenceCount', recurrenceDataLocal);
            }
            keyValueItemsDataSetter('scheduleType', reportAdditionalInfo?.Schedule?.ScheduleType);
            keyValueItemsDataSetter('timeZone', TimeZonePack[reportAdditionalInfo?.Schedule?.TimeZone]);
            if (
                !isNil(reportAdditionalInfo?.Schedule?.CronTime) &&
                !isEmpty(reportAdditionalInfo?.Schedule?.CronTime)
            ) {
                keyValueItemsDataSetter(
                    'startTime',
                    reportAdditionalInfo?.Schedule?.CronTime,
                    reportAdditionalInfo?.Schedule,
                );
            }
        }
    }, [recurrenceDataLocal, reportAdditionalInfo]);

    const getContent = (ReportType) => {
        // eslint-disable-next-line default-case
        switch (ReportType) {
            case ReportTypesLabels.Report:
                return (
                    <>
                        <KeyValueItemsGroup
                            itemsGroup={keyValueItemsGroupData.filter(({ isVisible }) => isVisible === true)}
                        />
                        <ColumnKPIsAndFiltersInfo
                            segmentFiltersData={segmentFiltersData}
                            reportType={ReportType}
                            reportInfo={reportAdditionalInfo}
                        />
                    </>
                );
            case ReportTypesLabels.CompositeReport:
                return (
                    <>
                        <CompositeReportGeneralInfoTabs
                            generalInfo={keyValueItemsGroupData.filter(({ isVisible }) => isVisible === true)}
                        />
                        <CompositeReportInfoTabs
                            segmentFiltersData={segmentFiltersData}
                            setSegmentFiltersData={setSegmentFiltersData}
                            compositeConfiguration={reportAdditionalInfo?.CompositeConfiguration}
                        />
                    </>
                );
            case ReportTypesLabels.CustomerJourneyReport:
                return (
                    <>
                        <KeyValueItemsGroup
                            itemsGroup={keyValueItemsGroupData.filter(({ isVisible }) => isVisible === true)}
                        />
                        <CustomerJourneyFiltersAndKPIs report={reportAdditionalInfo} blocks={customerJourney.blocks} />
                    </>
                );
        }
    };

    const init = () => {
        setIsLoading(true);
        doGetRequest(getReportById.request, {
            queryString: { reportId: ReportId, reportType: ReportType },
            successCallback: (data) => {
                const reportInfoData: AnyObject = cloneDeep(data);
                if (!isNil(data.Schedule)) {
                    if (!isNil(data.Schedule?.CronSchedule) && data.Schedule?.RecurrenceCount) {
                        reportInfoData.Schedule.ScheduleType = l.Repeat;
                        doPostRequest(getScheduleRunDatesRequest.request, {
                            requestBody: {
                                cronExpression: data.Schedule.CronSchedule,
                                startDate: data.Schedule.StartDate,
                                endDate: data.Schedule.EndDate,
                                timeZone: data.Schedule.TimeZone,
                            },
                            successCallback: (Data) => {
                                setRecurrenceDataLocal(Data);
                            },
                        });
                    } else {
                        reportInfoData.Schedule.ScheduleType = l.OneTime;
                    }
                }
                setReportAdditionalInfo(reportInfoData);
                setIsLoading(false);

                if (isCustomerJourneyReport) {
                    const workflowId = data.WorkflowReportConfiguration.WorkflowId;
                    const blockName = data.WorkflowReportConfiguration.BlockName;

                    doGetRequest(getCustomerJourneyPreviewById.request, {
                        queryString: { workflowId },
                        successCallback: (data) => {
                            const tmpConfig = JSON.parse(data?.Config ?? JSON_OBJECT);
                            const blockDisplayName = tmpConfig.Blocks.find(
                                (block) => block.Name === blockName,
                            )?.DisplayName;

                            setCustomerJourney({
                                blockName: blockDisplayName,
                                name: data.Name,
                                blocks: tmpConfig.Blocks ?? [],
                            });
                        },
                    }).then(() => {
                        setIsLoadingCustomerJourney(false);
                    });
                }
                if (isReport) {
                    getQueryConfigurationsListRequest.request(
                        dispatch,
                        queryConfigurations,
                        data,
                        {
                            shouldNotDispatch: true,
                            setData: (data) => {
                                dispatch(setDynamicSegmentMainSegmentInitialData(data));
                                setSegmentFiltersData(data);
                            },
                        },
                        t,
                    );
                }
            },
        });

        if (isReport) {
            setIsLoadingUsedIn(true);
            doPostRequest(getReportsByReport.request, {
                requestBody: {
                    reportId: ReportId,
                    reportType: ReportType,
                },
                successCallback: (Data) => {
                    setReportUsedInData(Data);
                    setIsLoadingUsedIn(false);
                },
            });
        }
    };

    const cleanUp = () => {
        return () => {
            getQueryConfigurationsListRequest.cancel('ReportInfoModal:getQueryConfigurationsList');
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    return (
        <>
            <Modal
                className={classNames('report-info-modal-c', {
                    'composite-report-info-modal': ReportType === ReportTypesLabels.CompositeReport,
                    'cj-report-info-modal': ReportType === ReportTypesLabels.CustomerJourneyReport,
                })}
                background="dark-background"
                position="center"
                title={
                    <div className="crm-object-list-modal-head">
                        <span className="crm-object-list-modal-h-text">{title}</span>
                        {isFunction(handleInfoModalActionClick) && !isNil(reportAdditionalInfo) && (
                            <div className="crm-object-list-modal-h-icons">
                                <ReportActionsButtons
                                    data={{ ...reportInfo, ...reportAdditionalInfo }}
                                    isInfoVisible={false}
                                    getReportVersionListPath={getReportVersionListPath}
                                    getScheduleReportPath={getScheduleReportPath}
                                    stopScheduleReportAction={stopScheduleReportAction}
                                    deleteReportAction={deleteReportAction}
                                    getCloneReportPath={getCloneReportPath}
                                    editReportAction={editReportAction}
                                    getExecuteReportPath={getExecuteReportPath}
                                    getEditReportPath={getEditReportPath}
                                    executeReportAction={executeReportAction}
                                    archiveReportAction={archiveReportAction}
                                    unarchiveReportAction={unarchiveReportAction}
                                    isViewMode={isViewMode}
                                    closeModal={onCancel}
                                />
                            </div>
                        )}
                    </div>
                }
                closable={false}
                onCancel={() => onCancel(ModalsActions.CLOSE)}
                closeOnClickOutside={true}
                visible={isVisibleModal}
                footer={
                    <Button appearance="minimal" onClick={() => onCancel(ModalsActions.CLOSE)}>
                        {t(l.Close)}
                    </Button>
                }
            >
                <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
                    {!isNil(reportAdditionalInfo) && getContent(ReportType)}
                </BusyLoader>
            </Modal>
        </>
    );
};

ReportInfoModal.propTypes = {
    reportInfo: PropTypes.object.isRequired,
    isVisibleModal: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    handleInfoModalActionClick: PropTypes.func,
    predefinedKeyValue: PropTypes.object,
};

export default memo(ReportInfoModal);
